import React, { useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Password as PrimePassword } from "primereact/password";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import "flag-icons/css/flag-icons.min.css";
import "./Registration.css";

// Import services
import AuthService from "../services/api/AuthService";
import logger from "../services/Logger";
import { countryPrefixes, CountryPrefix } from "../lib/countryPrefixes";
import GeasFaq from "../components/GeasFaq";

const validatePassword = (pwd: string) =>
  /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/.test(pwd);

const translateErrorMessage = (msg: string): string => {
  const map: Record<string, { regex: RegExp; template: string }> = {
    "Username is already taken": {
      regex: /Username '(.*?)'/,
      template: "Il codice fiscale '{username}' è già registrato.",
    },
    "Password is too short": {
      regex: /Password is too short/,
      template: "La password è troppo breve.",
    },
  };
  for (const { regex, template } of Object.values(map)) {
    const m = msg.match(regex);
    if (m) return template.replace("{username}", m[1] ?? "");
  }
  return "Errore durante la registrazione.";
};

/* ------------------------------------------------------------------ */
/*  Schema di validazione (Zod)                                       */
/* ------------------------------------------------------------------ */
const asciiNameRegex = /^[A-Za-z\s]+$/;
// E.164: il numero nazionale (senza prefisso internazionale) puo' arrivare fino a 15 cifre.
const phoneDigitsRegex = /^\d{6,15}$/;
const codiceFiscaleRegex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i;

const registrationSchema = z
  .object({
    nome: z.string().min(1, "Il nome è obbligatorio."),
    cognome: z.string().min(1, "Il cognome è obbligatorio."),
    email: z.string().email("Formato email non valido."),
    prefix: z.custom<CountryPrefix>(),
    telefono: z.string().min(1, "Il numero di telefono è obbligatorio."),
    codiceFiscale: z.string().regex(codiceFiscaleRegex, "Codice fiscale non valido."),
    birthdate: z.date({ message: "Data di nascita obbligatoria." }),
    genere: z.string().refine((val): val is "M" | "F" | "X" => ["M", "F", "X"].includes(val), {
      message: "Seleziona un genere valido.",
    }),
    password: z.string().refine(validatePassword, {
      message: "La password deve contenere almeno 8 caratteri, numeri e simboli.",
    }),
    confirmPassword: z.string(),
  })
  .superRefine((data, ctx) => {
    // nome + cognome solo lettere
    (["nome", "cognome"] as const).forEach((field) => {
      const val = data[field];
      if (!asciiNameRegex.test(val)) {
        const invalid = val.split("").filter((ch) => !/[A-Za-z\s]/.test(ch)).join("");
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `Il ${field} contiene caratteri non ammessi: '${invalid}'.`,
        });
      }
    });
    // telefono solo cifre
    if (!phoneDigitsRegex.test(data.telefono)) {
      const invalid = data.telefono.split("").filter((ch) => !/\d/.test(ch)).join("");
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["telefono"],
        message: `Il numero di telefono contiene caratteri non ammessi: '${invalid}'.`,
      });
    }
    // maggiorenne
    if (new Date().getFullYear() - data.birthdate.getFullYear() < 18) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["birthdate"],
        message: "Devi avere almeno 18 anni.",
      });
    }
    // password match
    if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["confirmPassword"],
        message: "Le password non coincidono.",
      });
    }
  });

type RegistrationForm = z.infer<typeof registrationSchema>;

/* ------------------------------------------------------------------ */
/*  Template Dropdown prefisso (bandiera + nome + prefisso)           */
/* ------------------------------------------------------------------ */
const prefixOptionTemplate = (option: CountryPrefix) => (
  <div className="flex align-items-center gap-2">
    <span className={`fi fi-${option.iso}`} style={{ width: "1.4rem", flex: "0 0 auto" }} />
    <span className="flex-1">{option.name}</span>
    <span className="text-color-secondary ml-2">+{option.dialCode}</span>
  </div>
);

const prefixValueTemplate = (option: CountryPrefix | undefined, props: { placeholder?: string }) => {
  if (!option) return <span>{props.placeholder}</span>;
  return (
    <div className="flex align-items-center gap-2">
      <span className={`fi fi-${option.iso}`} style={{ width: "1.4rem", flex: "0 0 auto" }} />
      <span>+{option.dialCode}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Componente                                                        */
/* ------------------------------------------------------------------ */
interface RegistrationProps {
  setActiveIndex: (index: number) => void;
  loginTabIndex: number;
}

const Registration: React.FC<RegistrationProps> = ({ setActiveIndex, loginTabIndex }) => {
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Modale di reinvio email di conferma (caso: CF registrato ma email non confermata)
  const [resendConfirmVisible, setResendConfirmVisible] = useState(false);
  const [isResendingConfirm, setIsResendingConfirm] = useState(false);
  const [resendConfirmCF, setResendConfirmCF] = useState("");

  const handleResendConfirmation = async () => {
    if (!resendConfirmCF) return;
    setIsResendingConfirm(true);
    try {
      await AuthService.resendConfirmationEmail(resendConfirmCF);
      toast.current?.show({
        severity: "success",
        summary: "Email inviata",
        detail:
          "Email di conferma inviata. Controlla la tua casella di posta, anche nella cartella SPAM.",
        life: 6000,
      });
      setResendConfirmVisible(false);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Errore",
        detail: error?.message || "Impossibile inviare l'email di conferma.",
        life: 6000,
      });
    } finally {
      setIsResendingConfirm(false);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RegistrationForm>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      codiceFiscale: "",
      password: "",
      confirmPassword: "",
      prefix: countryPrefixes[0],
      genere: undefined,
    } as Partial<RegistrationForm>,
    mode: "onBlur",
  });

  const onSubmit = async (data: RegistrationForm) => {
    setIsLoading(true);

    try {
      // Verifica se il codice fiscale esiste già
      const cfCheck = await AuthService.checkUserExists(data.codiceFiscale.toUpperCase());

      if (cfCheck.error) {
        toast.current!.show({
          severity: "warn",
          summary: "Attenzione",
          detail: cfCheck.error,
          life: 4000,
        });
        // Continua comunque - il backend gestirà l'errore se esiste
      } else if (cfCheck.exists && cfCheck.emailConfirmed === false) {
        // Registrato ma email NON confermata: mostra l'evidenza e offri il reinvio
        toast.current!.show({
          severity: "warn",
          summary: "Email non confermata",
          life: 12000,
          content: (
            <div className="flex" style={{ gap: "0.75rem", alignItems: "flex-start", width: "100%" }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: "1.5rem", color: "#cc8925" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: "0.5rem" }}>Email non confermata</div>
                <div>
                  Risulti già registrato, ma la tua email non è ancora stata confermata.
                  Controlla la tua casella di posta (anche nella cartella SPAM) e conferma
                  l'indirizzo per completare l'attivazione.
                  <br />
                  Non hai ricevuto l'email?{" "}
                  <a
                    onClick={() => {
                      setResendConfirmCF(data.codiceFiscale.toUpperCase());
                      setResendConfirmVisible(true);
                      toast.current?.clear();
                    }}
                    style={{
                      color: "var(--primary-color, #0082c2)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Reinvia email di conferma
                  </a>
                  .
                </div>
              </div>
            </div>
          ),
        });
        setIsLoading(false);
        return;
      } else if (cfCheck.exists) {
        toast.current!.show({
          severity: "error",
          summary: "Errore",
          life: 10000,
          content: (
            <div className="flex" style={{ gap: "0.75rem", alignItems: "flex-start", width: "100%" }}>
              <i className="pi pi-times-circle" style={{ fontSize: "1.5rem", color: "#ff5757" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: "0.5rem" }}>Errore</div>
                <div>
                  Codice fiscale già registrato. Effettua il login.
                  <br />
                  Se non ricordi la password effettua la procedura con{" "}
                  <a
                    onClick={() => {
                      toast.current?.clear();
                      setActiveIndex(loginTabIndex);
                    }}
                    style={{
                      color: "var(--primary-color, #0082c2)",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Password dimenticata
                  </a>
                  .
                </div>
              </div>
            </div>
          ),
        });
        setIsLoading(false);
        return;
      }

      // Converti genere da stringa a formato backend (M=true, F=false, X=null)
      const genereMap: Record<string, boolean | null> = { "M": true, "F": false, "X": null };

      const payload = {
        codiceFiscale: data.codiceFiscale.toUpperCase(),
        email: data.email.trim(),
        password: data.password,
        nome: data.nome.trim(),
        cognome: data.cognome.trim(),
        telefono: `+${(data.prefix ?? countryPrefixes[0]).dialCode} ${data.telefono}`,
        birthdate: `${data.birthdate.getFullYear()}-${String(data.birthdate.getMonth() + 1).padStart(2, '0')}-${String(data.birthdate.getDate()).padStart(2, '0')}`,
        genere: genereMap[data.genere]
      };

      logger.info("Attempting registration for:", payload.codiceFiscale);

      await AuthService.register(payload);

      toast.current!.show({
        severity: "success",
        summary: "Registrazione completata",
        content: (
          <div>
            Controlla la tua casella e-mail per confermare la registrazione.
            <br />
            Se non hai ricevuto l'email controlla la cartella dello spam.
            <br /><br />
            È anche possibile inviare nuovamente l'email di conferma cliccando su{' '}
            <strong>Invia di nuovo l'email di conferma</strong> presente nella pagina Login.
          </div>
        ),
        life: 6000,
      });
      reset();

      // Aspetta che il toast finisca prima di cambiare tab
      setTimeout(() => setActiveIndex(loginTabIndex), 6500);

    } catch (error: any) {
      logger.error("Registration failed:", error);
      toast.current!.show({
        severity: "error",
        summary: "Errore",
        detail: translateErrorMessage(error.message || "Errore"),
        life: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="registration-div">
      <Toast ref={toast} position="center" />
      <form className="registration-form grid p-fluid" onSubmit={handleSubmit(onSubmit)}>
        <div className="col-12 text-center mb-3">
          Già registrato?{" "}
          <a href="#login" onClick={e => { e.preventDefault(); setActiveIndex(loginTabIndex); }}>
            Effettua il Login
          </a>
        </div>
        <h2 className="col-12 text-center mb-4">Registrazione</h2>

        {/* Cognome */}
        <div className={`col-12 registration-input ${errors.cognome ? "error-field" : ""}`}>
          <FloatLabel>
            <Controller
              name="cognome"
              control={control}
              render={({ field }) => <InputText id="cognome" {...field} className="input-field" disabled={isLoading} />}
            />
            <label htmlFor="cognome">Cognome</label>
          </FloatLabel>
          {errors.cognome && <small className="p-error">{errors.cognome.message}</small>}
        </div>

        {/* Nome */}
        <div className={`col-12 registration-input ${errors.nome ? "error-field" : ""}`}>
          <FloatLabel>
            <Controller
              name="nome"
              control={control}
              render={({ field }) => <InputText id="nome" {...field} className="input-field" disabled={isLoading} />}
            />
            <label htmlFor="nome">Nome</label>
          </FloatLabel>
          {errors.nome && <small className="p-error">{errors.nome.message}</small>}
        </div>

        {/* Email */}
        <div className={`col-12 registration-input ${errors.email ? "error-field" : ""}`}>
          <FloatLabel>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <InputText id="email" {...field} className="input-field" disabled={isLoading} />}
            />
            <label htmlFor="email">Email</label>
          </FloatLabel>
          {errors.email && <small className="p-error">{errors.email.message}</small>}
        </div>

        {/* Telefono (InputGroup) */}
        <div className={`col-12 registration-input ${errors.telefono ? "error-field" : ""}`}>
          <label className="block mb-1">Telefono</label>
          <div className="p-inputgroup">
            <Controller
              name="prefix"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  options={countryPrefixes}
                  optionLabel="name"
                  filter
                  filterBy="name,dialCode"
                  filterPlaceholder="Cerca paese..."
                  resetFilterOnHide
                  itemTemplate={prefixOptionTemplate}
                  valueTemplate={prefixValueTemplate}
                  className="prefix-dropdown"
                  style={{ minWidth: "7.5rem", maxWidth: "9rem" }}
                  disabled={isLoading}
                />
              )}
            />
            <Controller
              name="telefono"
              control={control}
              render={({ field }) => (
                <InputText id="telefono" {...field} keyfilter="num" maxLength={15} className="input-field" disabled={isLoading} />
              )}
            />
          </div>
          {errors.telefono && <small className="p-error">{errors.telefono.message}</small>}
        </div>

        {/* Codice fiscale */}
        <div className={`col-12 registration-input ${errors.codiceFiscale ? "error-field" : ""}`}>
          <FloatLabel>
            <Controller
              name="codiceFiscale"
              control={control}
              render={({ field }) => <InputText id="codiceFiscale" {...field} className="input-field" disabled={isLoading} />}
            />
            <label htmlFor="codiceFiscale">Codice Fiscale</label>
          </FloatLabel>
          {errors.codiceFiscale && <small className="p-error">{errors.codiceFiscale.message}</small>}
        </div>

        {/* Data di nascita */}
        <div className={`col-12 registration-input ${errors.birthdate ? "error-field" : ""}`}>
          <label className="block mb-1">Data di nascita</label>
          <Controller
            name="birthdate"
            control={control}
            render={({ field }) => (
              <Calendar
                id="birthdate"
                {...field}
                value={field.value || null}
                onChange={(e) => field.onChange(e.value as Date)}
                showIcon
                dateFormat="dd/mm/yy"
                appendTo={document.body}
                className="input-field"
                disabled={isLoading}
              />
            )}
          />
          {errors.birthdate && <small className="p-error">{errors.birthdate.message}</small>}
        </div>

        {/* Genere */}
        <div className={`col-12 registration-input ${errors.genere ? "error-field" : ""}`}>
          <label className="block mb-1">Genere</label>
          <Controller
            name="genere"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                options={[
                  { label: "Maschio", value: "M" },
                  { label: "Femmina", value: "F" },
                  { label: "Altro", value: "X" },
                ]}
                placeholder="Seleziona..."
                className="dropdown-gender"
                disabled={isLoading}
              />
            )}
          />
          {errors.genere && <small className="p-error">Seleziona un genere</small>}
        </div>

        {/* Password */}
        <div className={`col-12 registration-input password-container ${errors.password ? "error-field" : ""}`}>
          <FloatLabel>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <PrimePassword id="password" toggleMask feedback={false} {...field} className="input-field" disabled={isLoading} />
              )}
            />
            <label htmlFor="password">Password</label>
          </FloatLabel>
          {errors.password && <small className="p-error">{errors.password.message}</small>}
        </div>

        {/* Conferma password */}
        <div className={`col-12 registration-input password-container ${errors.confirmPassword ? "error-field" : ""}`}>
          <FloatLabel>
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <PrimePassword id="confirmPassword" toggleMask feedback={false} {...field} className="input-field" disabled={isLoading} />
              )}
            />
            <label htmlFor="confirmPassword">Conferma Password</label>
          </FloatLabel>
          {errors.confirmPassword && <small className="p-error">{errors.confirmPassword.message}</small>}
        </div>

        {/* Submit */}
        <div className="col-12 mt-4">
          <Button type="submit" label="Registrati" icon="pi pi-check" className="submit-btn" loading={isLoading} />
        </div>
      </form>

      {/* FAQ dedicate GEAS (mostrate solo per il tenant GEAS) */}
      <GeasFaq />

      {/* Modale di reinvio email di conferma */}
      <Dialog
        header="Reinvia email di conferma"
        visible={resendConfirmVisible}
        onHide={() => setResendConfirmVisible(false)}
        style={{ width: "420px", maxWidth: "95vw" }}
        footer={
          <div>
            <Button
              label={isResendingConfirm ? "Invio in corso..." : "Reinvia email"}
              icon={isResendingConfirm ? "pi pi-spin pi-spinner" : "pi pi-envelope"}
              onClick={handleResendConfirmation}
              disabled={isResendingConfirm || !resendConfirmCF}
            />
            <Button
              label="Annulla"
              icon="pi pi-times"
              onClick={() => setResendConfirmVisible(false)}
              className="p-button-text"
              disabled={isResendingConfirm}
            />
          </div>
        }
      >
        <p style={{ marginTop: 0 }}>
          Invieremo nuovamente l'email di conferma al Codice Fiscale indicato.
          Controlla poi la casella di posta, <strong>anche nella cartella SPAM</strong>.
        </p>
        <FloatLabel>
          <InputText
            id="resendConfirmCF"
            value={resendConfirmCF}
            onChange={(e) => setResendConfirmCF(e.target.value.toUpperCase())}
            maxLength={16}
            className="w-full"
            disabled={isResendingConfirm}
          />
          <label htmlFor="resendConfirmCF">Codice Fiscale</label>
        </FloatLabel>
      </Dialog>
    </div>
  );
};

export default Registration;
